/**
 * alp-wishlist.js — Alpine replacement for ProductHelpers.attachWishlist.
 *
 * Design:
 *   - One Alpine.store('wishlist') holds the user's saved items.
 *   - The Swym list is fetched ONCE, deduped via a module-level promise, no
 *     matter how many wishlist blocks are on the page (ensure()).
 *   - Each button is an Alpine.data('wishlistButton') whose `saved` is a getter
 *     derived from the store — so a successful toggle updates store.items and
 *     every button re-renders reactively (no manual syncAllBlocks/DOM writes).
 *   - A button whose SSR product matches $store.product at init "follows the
 *     store" — its product/variant/metadata read live from the store so it
 *     tracks client-side nav. Card/sub-root buttons stay pinned to SSR values.
 *
 * Swym add/remove/fetch + banner are ported verbatim from product-helpers.js.
 * Assumes Alpine is already on the page (loaded by the product section).
 */
(function () {
  'use strict';
  if (window.__alpWishlistLoaded) return;
  window.__alpWishlistLoaded = true;

  function onSwymReady(fn) {
    if (typeof _swat !== 'undefined') fn();
    else {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push(fn);
    }
  }

  // --- banner (ported from product-helpers.js) ---
  function ensureBannerStyles() {
    if (document.getElementById('wishlist-banner-styles')) return;
    var styleEl = document.createElement('style');
    styleEl.id = 'wishlist-banner-styles';
    styleEl.textContent =
      '.wishlist-banner{position:fixed;top:20px;left:20px;z-index:9999;' +
      'display:flex;align-items:center;gap:12px;padding:12px 16px;max-width:320px;' +
      'background:#fff;border:1px solid #e5e7eb;' +
      'box-shadow:0 10px 15px -3px rgb(0 0 0 / 0.1),0 4px 6px -4px rgb(0 0 0 / 0.1);' +
      'transform:translateX(calc(-100% - 40px));transition:transform 300ms ease-out;}' +
      '.wishlist-banner.is-visible{transform:translateX(0)}' +
      '.wishlist-banner__image{width:48px;height:48px;object-fit:cover;flex-shrink:0;background:#f3f4f6}' +
      '.wishlist-banner__content{display:flex;flex-direction:column;min-width:0}' +
      '.wishlist-banner__message{font-size:14px;line-height:1.3;color:#111827}' +
      '.wishlist-banner__link{margin-top:2px;font-size:12px;color:#6b7280;text-decoration:underline}' +
      '.wishlist-banner__link:hover{color:#111827}' +
      '@media (prefers-reduced-motion: reduce){.wishlist-banner{transition:none}}';
    document.head.appendChild(styleEl);
  }

  var bannerEl = null, bannerHideTimeout = null, bannerUnmountTimeout = null;

  function ensureBanner() {
    if (bannerEl) return bannerEl;
    ensureBannerStyles();
    bannerEl = document.createElement('div');
    bannerEl.className = 'wishlist-banner';
    bannerEl.setAttribute('role', 'status');
    bannerEl.setAttribute('aria-live', 'polite');
    bannerEl.setAttribute('aria-atomic', 'true');
    bannerEl.hidden = true;
    bannerEl.innerHTML =
      '<img class="wishlist-banner__image" alt="" width="48" height="48" />' +
      '<div class="wishlist-banner__content">' +
      '<span class="wishlist-banner__message"></span>' +
      '<a class="wishlist-banner__link" href="/pages/swym-wishlist">View your list</a>' +
      '</div>';
    document.body.appendChild(bannerEl);
    return bannerEl;
  }

  function showWishlistBanner(action, title, src) {
    var el = ensureBanner();
    var img = el.querySelector('.wishlist-banner__image');
    var msg = el.querySelector('.wishlist-banner__message');
    img.src = src || '';
    img.alt = title || '';
    img.hidden = !src;
    msg.textContent = (title || 'Item') + (action === 'add' ? ' added' : ' removed');
    if (bannerHideTimeout) clearTimeout(bannerHideTimeout);
    if (bannerUnmountTimeout) clearTimeout(bannerUnmountTimeout);
    el.hidden = false;
    void el.offsetWidth; // force reflow so the transition runs from off-screen
    el.classList.add('is-visible');
    bannerHideTimeout = setTimeout(function () {
      el.classList.remove('is-visible');
      bannerUnmountTimeout = setTimeout(function () { el.hidden = true; }, 300);
    }, 10000);
  }

  function pushAddToWishlist(variantId, title) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_wishlist',
        ecommerce: { items: [{ item_id: String(variantId), item_name: title || '' }] },
      });
    } catch (e) {
      /* analytics must never break the page */
    }
  }

  // Module-level dedup guard: the Swym list is fetched at most once per page.
  var fetchPromise = null;
  var loaded = false;

  document.addEventListener('alpine:init', function () {
    Alpine.store('wishlist', {
      items: [],

      // Single fetch shared across every block. First caller kicks off the
      // request; everyone else awaits the same promise (or the cached list).
      ensure() {
        if (loaded) return Promise.resolve(this.items);
        if (fetchPromise) return fetchPromise;
        var self = this;
        fetchPromise = new Promise(function (resolve) {
          onSwymReady(function () {
            _swat.fetchWrtEventTypeET(function (products) {
              self.items = products || [];
              loaded = true;
              resolve(self.items);
            }, 4);
          });
        });
        return fetchPromise;
      },

      isSaved(productId, variantId) {
        if (!productId || !variantId) return false;
        var pid = String(productId), vid = String(variantId);
        return this.items.some(function (p) {
          return String(p.empi) === pid && String(p.epi) === vid;
        });
      },

      // Add or remove on Swym, then mutate items so all buttons re-derive.
      // Resolves with the new saved state; rejects on Swym error so the
      // caller can release its saving flag without changing UI.
      toggle(ref) {
        var self = this;
        var productId = String(ref.productId || '');
        var variantId = String(ref.variantId || '');
        if (!productId || !variantId) return Promise.reject(new Error('wishlist: missing ids'));
        var url = ref.url ? (ref.url.indexOf('http') === 0 ? ref.url : window.location.origin + ref.url) : '';
        var data = { empi: parseInt(productId, 10), epi: parseInt(variantId, 10), du: url, et: 4 };

        return new Promise(function (resolve, reject) {
          onSwymReady(function () {
            if (ref.saved) {
              _swat.removeFromWishList(data, function () {
                self.items = self.items.filter(function (p) {
                  return !(String(p.empi) === productId && String(p.epi) === variantId);
                });
                showWishlistBanner('remove', ref.title, ref.image);
                resolve(false);
              }, reject);
            } else {
              _swat.addToWishList(data, function () {
                self.items = self.items.concat([{ empi: productId, epi: variantId }]);
                showWishlistBanner('add', ref.title, ref.image);
                pushAddToWishlist(variantId, ref.title);
                resolve(true);
              }, reject);
            }
          });
        });
      },
    });

    Alpine.data('wishlistButton', function (init) {
      init = init || {};
      return {
        // SSR snapshot — the fallback identity, and what card/sub-root
        // wishlists use permanently.
        ssrProductId: String(init.productId || ''),
        ssrVariantId: String(init.variantId || ''),
        ssrUrl: init.url || '',
        ssrTitle: init.title || 'Item',
        ssrImage: init.image || '',
        // Set once at init: is this the page's product wishlist (vs a card)?
        // Page wishlists follow $store.product across client-side nav so the
        // heart tracks whatever product is currently loaded.
        followsStore: false,
        saving: false,

        init() {
          var store = Alpine.store('product');
          this.followsStore = !!(store && String(store.id) === this.ssrProductId);
          Alpine.store('wishlist').ensure();
        },

        // All identity/metadata read live from the store when this is the page
        // wishlist (so they update on nav), else from the SSR snapshot.
        get productId() {
          var s = Alpine.store('product');
          return this.followsStore && s && s.id ? String(s.id) : this.ssrProductId;
        },
        get variantId() {
          var sv = Alpine.store('selectedVariant');
          return this.followsStore && sv && sv.id ? String(sv.id) : this.ssrVariantId;
        },
        get url() {
          var s = Alpine.store('product');
          return this.followsStore && s && s.url ? s.url : this.ssrUrl;
        },
        get title() {
          var s = Alpine.store('product');
          return this.followsStore && s && s.title ? s.title : this.ssrTitle;
        },
        get image() {
          var s = Alpine.store('product');
          return this.followsStore && s && s.featuredImage ? s.featuredImage : this.ssrImage;
        },

        get saved() {
          return Alpine.store('wishlist').isSaved(this.productId, this.variantId);
        },

        toggle() {
          if (this.saving) return;
          var self = this;
          this.saving = true;
          Alpine.store('wishlist')
            .toggle({
              productId: this.productId,
              variantId: this.variantId,
              url: this.url,
              title: this.title,
              image: this.image,
              saved: this.saved,
            })
            .catch(function (err) { console.warn('[wishlist] toggle failed', err); })
            .finally(function () { self.saving = false; });
        },
      };
    });
  });
})();
